import { InputField } from '@/components/form/InputField';
import ToggleableRow from '@/components/purchase/checkout/ToggleableRow';
import { TransText } from '@/i18n/trans/text';
import type { SendToEmailFormValues } from '@/types/sendTicketsToEmail';
import { Button, Checkbox, Icons, Typography } from '@fleet/webshop-ui';
import { type FC, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

const PurchaserEmailSection: FC = () => {
  const { control, setValue } = useFormContext<SendToEmailFormValues>();

  const emailOverride =
    useWatch({
      control,
      name: 'purchaser.emailOverride',
    }) || [];

  const sendAllTickets = useWatch({
    control,
    name: '_meta.sendAllTickets',
  });

  const handleToggle = useCallback(() => {
    setValue('_meta.sendAllTickets', !sendAllTickets);
  }, [sendAllTickets, setValue]);

  const handleAddEmail = useCallback(() => {
    setValue('purchaser.emailOverride', [...emailOverride, '']);
  }, [emailOverride, setValue]);

  const handleRemoveEmail = useCallback(
    (index: number) => {
      setValue(
        'purchaser.emailOverride',
        emailOverride.filter((_, i) => i !== index)
      );
    },
    [emailOverride, setValue]
  );

  const handleClearEmail = useCallback(
    (index: number) => {
      setValue(`purchaser.emailOverride.${index}`, '');
    },
    [setValue]
  );

  return (
    <div className="flex flex-col gap-2">
      <ToggleableRow
        isActive={sendAllTickets}
        onClick={handleToggle}
        className="flex flex-row gap-3"
      >
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            isSelected={sendAllTickets}
            onChange={() => handleToggle()}
          />
        </div>
        <div className="flex flex-col">
          <Typography variant="body1-bold">
            <TransText i18nKey="allTickets" />
          </Typography>
          <Typography variant="body2" className="text-neutral">
            <TransText
              i18nKey="emailsCount"
              values={{ count: emailOverride.length }}
            />
          </Typography>
        </div>
      </ToggleableRow>

      <div className="ml-10 flex flex-col gap-3">
        {emailOverride.map((value, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="flex-1">
              <InputField
                name={`purchaser.emailOverride.${index}`}
                label={
                  <TransText
                    i18nKey="emailToSendAllTicketsTo"
                    values={{ count: index + 1 }}
                  />
                }
                hideLabel
                endAdornment={
                  value ? (
                    <Button
                      variant="ghost"
                      onClick={() => handleClearEmail(index)}
                      data-testid={`clear-email-${index}`}
                      aria-label="Clear email"
                      className="text-dark"
                    >
                      <Icons.xSmall />
                    </Button>
                  ) : null
                }
              />
            </div>

            {emailOverride.length > 1 && (
              <Button
                variant="secondary"
                size="small"
                className="aspect-square w-fit rounded-full p-2"
                type="button"
                onClick={() => handleRemoveEmail(index)}
                data-testid={`remove-purchaser-email-${index}`}
              >
                <Icons.minus height={16} width={16} />
              </Button>
            )}
          </div>
        ))}

        <Button
          variant="ghost"
          type="button"
          onClick={handleAddEmail}
          data-testid="add-more-emails-button"
        >
          <Icons.addCircle height={20} width={20} />
          <TransText i18nKey="addMoreEmails" />
        </Button>
      </div>
    </div>
  );
};

export default PurchaserEmailSection;
